import React, { useEffect, useState } from 'react';
import { Switch, Tag, Space, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface TaxExemptionToggleProps {
  customer: { id: number; is_tax_exempt: boolean | number; tax_exempt_id: string | null };
  onTaxRateChange: (newRate: number) => void;
  /**
   * When true, render an editable invoice-level tax-exemption switch instead of
   * the read-only customer badge. Used on the admin invoice page so staff can
   * override exemption for a specific invoice (e.g. a fleet/government job).
   */
  editable?: boolean;
  /** Current invoice-level exemption flag (controlled). */
  invoiceTaxExempt?: boolean;
  /**
   * Persist hook for the invoice-level override. Receives the new flag; should
   * return a promise that resolves on success / rejects on failure.
   */
  onToggleExempt?: (next: boolean) => Promise<void> | void;
}

export const TaxExemptionToggle: React.FC<TaxExemptionToggleProps> = ({
  customer,
  onTaxRateChange,
  editable = false,
  invoiceTaxExempt = false,
  onToggleExempt,
}) => {
  const customerExempt = Boolean(customer.is_tax_exempt);
  // Effective exemption: an invoice-level override OR the customer default.
  const effectiveExempt = editable ? invoiceTaxExempt || customerExempt : customerExempt;
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (effectiveExempt) onTaxRateChange(0);
  }, [effectiveExempt, onTaxRateChange]);

  // Editable invoice-level override mode (admin invoice page).
  if (editable) {
    const handleChange = async (next: boolean) => {
      if (!onToggleExempt) return;
      setSaving(true);
      try {
        await onToggleExempt(next);
      } finally {
        setSaving(false);
      }
    };
    return (
      <div
        data-testid="tax-exempt-toggle"
        style={{ background: '#f6ffed', padding: 8, borderRadius: 4 }}
      >
        <Space size="small" align="center">
          <Switch
            checked={invoiceTaxExempt}
            disabled={saving || customerExempt}
            onChange={handleChange}
            data-testid="tax-exempt-switch"
          />
          <Text strong>Tax Exempt</Text>
          {saving && <LoadingOutlined />}
          {customerExempt && (
            <Text type="secondary">(customer is tax-exempt — {customer.tax_exempt_id || 'no ID'})</Text>
          )}
        </Space>
      </div>
    );
  }

  // Read-only customer badge (estimates / work orders).
  if (!customerExempt) return null;
  return (
    <Tag color="green" data-testid="tax-exempt-badge">
      Tax Exempt — {customer.tax_exempt_id || 'no ID'}
    </Tag>
  );
};

export default TaxExemptionToggle;
